/**
 * Labstep
 *
 * @module components/Molecule/Item
 * @desc Item to show comment molecule
 */

import EntityActionCreate from 'labstep-web/components/Entity/Action/Create';
import { EntityActionUpdateContainer as EntityActionUpdate } from 'labstep-web/components/Entity/Action/Update';
import { useHasAccess } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { MoleculeActionEditContainer as MoleculeActionEdit } from 'labstep-web/components/Molecule/Action/Edit';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import type { ActionComponentProps } from 'labstep-web/core/Action/Component/types';
import { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import { Molecule } from 'labstep-web/models/molecule.model';
import type React from 'react';
import { Image } from 'semantic-ui-react';
import styles from './styles.module.scss';
import type { MoleculeItemProps } from './types';

const drawMoleculeActionComponentProps = {
  type: 'text',
  elementProps: { placeholder: true },
  text: 'Draw structure',
} as ActionComponentProps;

const actionComponentProps = {
  type: 'icon',
  icon: 'external',
  elementProps: { color: 'blue' },
  text: '',
} as ActionComponentProps;

export const MoleculeItem: React.FC<MoleculeItemProps> = ({
  parent,
  createProps,
  text,
}) => {
  const { molecule } = parent;
  const canEdit = useHasAccess(
    parent.entityName,
    parent.idAttr,
    Action.edit,
    'molecule',
  ) as boolean;

  const finalDrawMoleculeActionComponentProps = {
    ...drawMoleculeActionComponentProps,
  };
  if (text) {
    finalDrawMoleculeActionComponentProps.text = text;
  }

  return (
    <MoleculeActionEdit
      molecule={molecule || new Molecule()}
      canEdit={canEdit}
      viewComponent={({ toggleModal }): React.ReactElement => {
        let expandIconComponent = null;
        let mainComponent = null;
        const emptyActionComponent = (
          <ActionComponent
            type="text"
            elementProps={{
              placeholder: true,
            }}
            text="Empty structure"
          />
        );
        if (createProps) {
          if (canEdit) {
            mainComponent = (
              <EntityActionCreate
                parentName={createProps.parentName}
                parentId={createProps.parentId}
                entityName={Metadata.entityName}
                actionComponentProps={
                  finalDrawMoleculeActionComponentProps
                }
                body={{
                  ...createProps.createBody,
                  molecule: {
                    name: 'Untitled',
                    data: '',
                  },
                  type: MetadataType.molecule,
                }}
                options={{ onSuccess: toggleModal }}
              />
            );
          } else {
            mainComponent = emptyActionComponent;
          }
        } else if (molecule) {
          if (molecule.svg && !text) {
            expandIconComponent = (
              <ActionComponent
                onClick={toggleModal}
                {...actionComponentProps}
              />
            );
            const src = URL.createObjectURL(
              new Blob([molecule.svg], { type: 'image/svg+xml' }),
            );
            mainComponent = (
              <Image
                src={src}
                alt="Molecule structure"
                className={styles.moleculeImage}
                onClick={toggleModal}
              />
            );
          } else if (!canEdit) {
            mainComponent = emptyActionComponent;
          } else {
            mainComponent = (
              <ActionComponent
                onClick={toggleModal}
                {...finalDrawMoleculeActionComponentProps}
              />
            );
          }
        } else {
          mainComponent = (
            <EntityActionUpdate
              entityName={parent.entityName}
              id={parent.id}
              body={{
                molecule: {
                  name: 'Untitled',
                  data: '',
                },
              }}
              actionComponentProps={
                finalDrawMoleculeActionComponentProps
              }
              options={{ onSuccess: toggleModal }}
            />
          );
        }
        return (
          <div className={styles.container}>
            <div className={styles.expandIcon}>
              {expandIconComponent}
            </div>
            {mainComponent}
          </div>
        );
      }}
    />
  );
};
